import type { ReactNode } from 'react';
import React, { useRef } from 'react';

import type { AlertProps } from '../../components/Alert/Alert';
import { backgrounds } from '../../common/backgrounds';
import { sizing } from '../../common/sizing';
import { Alert } from '../../components/Alert/Alert';
import { useUtilityVisibility } from '../../controls/UtilityControl/useUtilityVisibility';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';

const visualizerZIndices = {
  toolbar: 4,
  display: 1,
  properties: 2,
};

const visualizerPropertiesWidth = {
  expanded: {
    desktop: 180,
    notDesktop: 200,
  },
  collapsed: 40,
};

const visualizerPadding = {
  y: 84,
  x: 48,
};

/** How far the floating chrome sits in from the frame's edge. */
const visualizerInset = 8;

/** What the floating toolbar occupies from the frame's top: inset plus its own height. */
const visualizerToolbarClearance = 44;

/** The chrome floats on the canvas the way the diagram's chips do: same fill, ring, and drops. */
const visualizerPanel = {
  backgroundColor: colors.bgApplicationLight,
  borderRadius: '$8',
  boxShadow: shadows.connectionLight,

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.connectionDark,
  },
};

// Runs from the frame's left edge to the rail, floating over the canvas as the rail does.
const VisualizerToolbar = styled('div', {
  position: 'absolute',
  top: `$${visualizerInset}`,
  left: `$${visualizerInset}`,
  right: `$${visualizerInset}`,
  zIndex: visualizerZIndices.toolbar,
  display: 'flex',
  alignItems: 'center',
  gap: '$12',
  padding: '$6',
  transition: 'right 0.3s ease-in-out',
  ...visualizerPanel,

  variants: {
    show: {
      true: {
        '@desktop': {
          right: `$${visualizerPropertiesWidth.expanded.desktop + visualizerInset * 2}`,
        },

        '@notDesktop': {
          right: `$${visualizerPropertiesWidth.expanded.notDesktop + visualizerInset * 2}`,
        },
      },
      false: {
        right: `$${visualizerPropertiesWidth.collapsed + visualizerInset * 2}`,
      },
    },
  },
});
VisualizerToolbar.displayName = 'VisualizerToolbar';

const VisualizerToolbarEnd = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$8',
  marginLeft: 'auto',
});
VisualizerToolbarEnd.displayName = 'VisualizerToolbarEnd';

const VisualizerDisplay = styled('div', {
  margin: '0 auto',
  zIndex: visualizerZIndices.display,
});
VisualizerDisplay.displayName = 'VisualizerDisplay';

const VisualizerScroll = styled('div', {
  display: 'flex',
  flex: 1,
  paddingTop: `$${visualizerPadding.y}`,
  paddingRight: `$${visualizerPadding.x}`,
  paddingBottom: `$${visualizerPadding.y}`,
  paddingLeft: `$${visualizerPadding.x}`,
  overflow: 'auto',
  minHeight: 0,
  userSelect: 'none',
  // Keeps the centred display moving in step with the rail's width and the toolbar's edge.
  transition: 'padding-right 0.3s ease-in-out',

  variants: {
    // The toolbar floats over the canvas's top, so the display clears it and keeps its usual room
    // for chips and trace lines.
    withToolbar: {
      true: {
        paddingTop: `$${visualizerPadding.y + visualizerToolbarClearance}`,
      },
      false: {},
    },
    show: {
      true: {
        '@desktop': {
          paddingRight: `$${visualizerPropertiesWidth.expanded.desktop + visualizerPadding.x + visualizerInset}`,
        },

        '@notDesktop': {
          paddingRight: `$${visualizerPropertiesWidth.expanded.notDesktop + visualizerPadding.x + visualizerInset}`,
        },
      },
      false: {
        paddingRight: `$${visualizerPropertiesWidth.collapsed + visualizerPadding.x + visualizerInset}`,
      },
    },
  },
});
VisualizerScroll.displayName = 'VisualizerScroll';

// Straddles the panel's edge the way FlexiColumns' toggles straddle a column boundary.
const VisualizerPropertiesToggle = styled(UtilityControl, {
  position: 'absolute',
  top: '50%',
  left: 0,
  transform: 'translate(-50%, -50%)',
  zIndex: 1,

  '&&': {
    backgroundColor: colors.bgNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgNeutralDark,
    },
  },
});
VisualizerPropertiesToggle.displayName = 'VisualizerPropertiesToggle';

const VisualizerPropertiesInner = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  padding: '$8',
  overflow: 'auto',
  gap: '$12',
});
VisualizerPropertiesInner.displayName = 'VisualizerPropertiesInner';

const VisualizerPropertiesContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '100%',
  minHeight: 0,
  overflow: 'hidden',
  transition: 'width 0.3s ease-in-out',
  ...visualizerPanel,

  variants: {
    show: {
      true: {
        '@notDesktop': {
          width: `$${visualizerPropertiesWidth.expanded.notDesktop}`,
        },

        '@desktop': {
          width: `$${visualizerPropertiesWidth.expanded.desktop}`,
        },
      },
      false: {
        width: `$${visualizerPropertiesWidth.collapsed}`,
      },
    },
  },
});
VisualizerPropertiesContent.displayName = 'VisualizerPropertiesContent';

const VisualizerProperties = styled('div', {
  position: 'absolute',
  top: `$${visualizerInset}`,
  right: `$${visualizerInset}`,
  bottom: `$${visualizerInset}`,
  zIndex: visualizerZIndices.properties,
  display: 'flex',
});
VisualizerProperties.displayName = 'VisualizerProperties';

const VisualizerArea = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  heightAll: 'min-content',
  overflow: 'hidden',
  background: backgrounds.dottedLight,
  backgroundColor: colors.bgNeutralLight,
  strokeAll: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    background: backgrounds.dottedDark,
    backgroundColor: colors.bgNeutralDark,
    strokeAll: colors.strokeNeutralDark,
  },

  '&:first-child': {
    borderRadiusTop: '$10',
  },

  '&:last-child': {
    borderRadiusBottom: '$10',
  },
});
VisualizerArea.displayName = 'VisualizerArea';

const VisualizerAlert = styled(Alert, {
  borderRadius: '0 !important',

  '&:first-child': {
    borderRadiusTop: '$10 !important',
  },

  '&:last-child': {
    borderRadiusBottom: '$10 !important',
  },
});
VisualizerAlert.displayName = 'VisualizerAlert';

const VisualizerContainer = styled('div', {
  width: '100%',
  heightAll: 'min-content',
  padding: sizing.squish,
});
VisualizerContainer.displayName = 'VisualizerContainer';

export type VisualizerProps = {
  /**
   * The alert to display on the visualizer.
   */
  alert?: AlertProps;
  /**
   * The core of the visualizer where the diagrams/flows appear.
   */
  display: ReactNode;
  /**
   * The properties of what is being displayed such as legends and glossaries.
   */
  properties?: {
    show?: boolean;
    setShow?: (show: boolean) => void;
    content: ReactNode;
  };
  /**
   * Metadata about the visualizer such as the last updated time, shown at the end of the toolbar.
   */
  metadata?: ReactNode;
  /**
   * A bar floating over the display for controls that change what the diagram shows.
   */
  toolbar?: ReactNode;
};

export function Visualizer({
  alert,
  display,
  metadata,
  properties,
  toolbar,
  ...remaining
}: VisualizerProps) {
  // The toggle shows only while the rail is being interacted with, as FlexiColumns' controls do.
  const propertiesRef = useRef<HTMLDivElement>(null);
  const { visible: toggleVisible } = useUtilityVisibility({ containerRef: propertiesRef });

  const hasProperties = Boolean(properties && properties.content);

  return (
    <VisualizerContainer {...remaining}>
      {alert && <VisualizerAlert {...alert} />}
      <VisualizerArea>
        {(toolbar || metadata) && (
          <VisualizerToolbar show={hasProperties ? properties?.show : undefined}>
            {toolbar}
            {metadata && <VisualizerToolbarEnd>{metadata}</VisualizerToolbarEnd>}
          </VisualizerToolbar>
        )}
        <VisualizerScroll show={properties?.show} withToolbar={Boolean(toolbar || metadata)}>
          <VisualizerDisplay>{display}</VisualizerDisplay>
        </VisualizerScroll>
        {hasProperties && properties && (
          <VisualizerProperties ref={propertiesRef}>
            <VisualizerPropertiesToggle
              placement="sides"
              variant="neutral"
              visible={toggleVisible}
              icon={properties.show ? 'pane-end-collapse' : 'pane-end-expand'}
              label={`${properties.show ? 'Hide' : 'Show'} properties`}
              onClick={() => properties.setShow?.(!properties.show)}
            />
            <VisualizerPropertiesContent show={properties.show}>
              <VisualizerPropertiesInner>{properties.content}</VisualizerPropertiesInner>
            </VisualizerPropertiesContent>
          </VisualizerProperties>
        )}
      </VisualizerArea>
    </VisualizerContainer>
  );
}
